/**
 * Shared spherical geometry for the map layers.
 *
 * `destinationPoint` lived inside App.tsx, used only by the drone FOV cone. It moved here
 * when the geolocation preview needed an accuracy circle: the repo already carries three
 * TypeScript haversines and two destination-point copies, and adding a third would have
 * been the wrong direction. App.tsx and GeolocatedPreview cannot import from each other
 * without a cycle, so a shared module is the only home that works for both.
 */
const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;
export const EARTH_R = 6371000;

/** Point at `distanceM` along `bearingDeg` from (lat, lng). Returns `[lng, lat]`. */
export function destinationPoint(
  lat: number, lng: number, bearingDeg: number, distanceM: number,
): [number, number] {
  const latR = lat * DEG2RAD;
  const brng = bearingDeg * DEG2RAD;
  const angDist = distanceM / EARTH_R;
  const sinAng = Math.sin(angDist);
  const cosAng = Math.cos(angDist);
  const sinLat = Math.sin(latR);
  const cosLat = Math.cos(latR);
  const lat2 = Math.asin(sinLat * cosAng + cosLat * sinAng * Math.cos(brng));
  const lng2 = lng * DEG2RAD + Math.atan2(
    Math.sin(brng) * sinAng * cosLat,
    cosAng - sinLat * Math.sin(lat2),
  );
  return [((lng2 * RAD2DEG + 540) % 360) - 180, lat2 * RAD2DEG];
}

/** Closed ring approximating a circle, as `[lng, lat]` pairs. First vertex repeated last. */
export function circleRing(
  lat: number, lng: number, radiusM: number, segments = 48,
): [number, number][] {
  const n = Math.max(8, Math.min(128, Math.round(segments)));
  const ring: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    ring.push(destinationPoint(lat, lng, (360 * i) / n, radiusM));
  }
  ring.push(ring[0]);
  return ring;
}
